import React, { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";
import Loading from "../../../common/components/loading/loading";
import * as XLSX from "xlsx";
import { formatDate } from "../../../common/services/utilities";

const SettlementsCombinedTable = ({
  settlements,
  isLoading,
  title,
  currencySymbol,
}) => {
  const { t } = useTranslation(["marketdata"]);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "marketDate",
        headerName: t("MarketDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "termName",
        headerName: t("TermName"),
        sortable: true,
        filter: true,
      },
      {
        field: "beginDate",
        headerName: t("BeginDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "endDate",
        headerName: t("EndDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "ngxPowerSettle",
        headerName: t("NgxPowerSettle"),
        sortable: true,
        filter: true,
      },
      {
        field: "ngxGasSettle",
        headerName: t("NgxGasSettle"),
        sortable: true,
        filter: true,
      },
      {
        field: "nymexGasSettle",
        headerName: t("NymexGasSettle"),
        sortable: true,
        filter: true,
      },
      {
        field: "nymexCrudeSettle",
        headerName: t("NymexCrudeSettle"),
        sortable: true,
        filter: true,
      },
      {
        field: "heatRate",
        headerName: t("HeatRate"),
        sortable: true,
        filter: true,
      },
      {
        field: "exchangeRate",
        headerName: t("ExchangeRate"),
        sortable: true,
        filter: true,
      },
    ],
    [t]
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
    }),
    []
  );

  // Map to view model
  const mapToViewModel = useCallback(
    (settlementsUnsorted) => {
      const settlements = settlementsUnsorted.sort((a, b) => {
        // Compare marketDate ascending
        if (a.marketDate < b.marketDate) return -1;
        if (a.marketDate > b.marketDate) return 1;

        // If marketDate is the same, compare endDate ascending
        if (a.endDate < b.endDate) return -1;
        if (a.endDate > b.endDate) return 1;

        // If endDate is also the same, compare beginDate descending
        if (a.beginDate > b.beginDate) return -1;
        if (a.beginDate < b.beginDate) return 1;

        // If all dates are equal, maintain original order
        return 0;
      });

      return settlements.map((settlement) => ({
        marketDate: formatDate(settlement.marketDate) ?? null,
        termName:
          settlement.NYMEXCrude_termName ??
          settlement.NYMEXGas_termName ??
          settlement.NGXGas_termName ??
          settlement.NGXPower_termName ??
          null,
        beginDate: formatDate(settlement.beginDate) ?? null,
        endDate: formatDate(settlement.endDate) ?? null,
        ngxPowerSettle: settlement.NGXPower_current
          ? currencySymbol + settlement.NGXPower_current.toFixed(2)
          : "",
        ngxGasSettle: settlement.NGXGas_current
          ? currencySymbol + settlement.NGXGas_current.toFixed(4)
          : "",
        nymexGasSettle: settlement.NYMEXGas_settlePrice
          ? currencySymbol + settlement.NYMEXGas_settlePrice.toFixed(4)
          : "",
        nymexCrudeSettle: settlement.NYMEXCrude_settlePrice
          ? currencySymbol + settlement.NYMEXCrude_settlePrice.toFixed(4)
          : "",
        heatRate:
          settlement.NGXPower_current &&
          settlement.NGXGas_current &&
          settlement.NGXGas_current !== 0
            ? (settlement.NGXPower_current / settlement.NGXGas_current).toFixed(
                2
              )
            : "",
        exchangeRate: settlement.FX_Rate
          ? currencySymbol + settlement.FX_Rate.toFixed(4)
          : "",
      }));
    },
    [currencySymbol]
  );

  // Export to Excel
  const downloadToExcel = useCallback(() => {
    const excelData = settlements.map((settlement) => ({
      marketDate: formatDate(settlement.marketDate) ?? null,
      termName:
        settlement.NYMEXCrude_termName ??
        settlement.NYMEXGas_termName ??
        settlement.NGXGas_termName ??
        settlement.NGXPower_termName ??
        null,
      beginDate: formatDate(settlement.beginDate) ?? null,
      endDate: formatDate(settlement.endDate) ?? null,
      ngxPowerSettle: settlement.NGXPower_current
        ? settlement.NGXPower_current.toFixed(2)
        : "",
      ngxGasSettle: settlement.NGXGas_current
        ? settlement.NGXGas_current.toFixed(4)
        : "",
      nymexGasSettle: settlement.NYMEXGas_settlePrice
        ? settlement.NYMEXGas_settlePrice.toFixed(4)
        : "",
      nymexCrudeSettle: settlement.NYMEXCrude_settlePrice
        ? settlement.NYMEXCrude_settlePrice.toFixed(4)
        : "",
      heatRate:
        settlement.NGXPower_current &&
        settlement.NGXGas_current &&
        settlement.NGXGas_current !== 0
          ? (settlement.NGXPower_current / settlement.NGXGas_current).toFixed(2)
          : "",
      exchangeRate: settlement.FX_Rate ? settlement.FX_Rate.toFixed(4) : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, title + ".xlsx");
  }, [settlements, title]);

  // Create a getRowId function to ensure stable row identification
  const getRowId = useCallback((params) => {
    // Create a unique ID using a combination of key fields
    return `${params.data.marketDate}-${params.data.termName}-${params.data.beginDate}-${params.data.endDate}`;
  }, []);

  if (isLoading) return <Loading />;

  const tableData = mapToViewModel(settlements);

  return (
    <div>
      <div className="row">
        <div className="col-md-10">
          <h6>{title}</h6>
        </div>
        <div className="col-md-2" style={{ alignSelf: "flex-end" }}>
          <button
            style={{ float: "right" }}
            className="btn btn-sm btn-primary no-print"
            onClick={downloadToExcel}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faDownload} /> {t("Excel")}
          </button>
        </div>
      </div>
      <div className="col-md-12" style={{ height: "100%" }}>
        <ThemeableAgGrid
          className="table-extra-small"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={tableData}
          getRowId={getRowId}
          domLayout="normal"
          animateRows={true}
        />
      </div>
    </div>
  );
};

export default SettlementsCombinedTable;
